import type { FgConfig } from '../parser/types'

export type Severity = 'critical' | 'high' | 'medium' | 'low' | 'info'

export const SEVERITY_ORDER: Record<Severity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
}

/**
 * One problem found in a configuration.
 *
 * IMPORTANT, and it is a legal constraint rather than a style one: `cis` and `pci`
 * carry reference NUMBERS only. CIS Benchmark descriptive text, rationale and audit
 * procedures are licensed (CIS sells SecureSuite for exactly that), and the PCI DSS
 * standard text is copyrighted by the PCI SSC. Citing "CIS 3.2" and "PCI DSS 1.2.7"
 * is fine; reproducing what they say is not. Every word of `title`, `detail` and
 * `remediation` must be our own.
 */
export interface Finding {
  ruleId: string
  severity: Severity
  title: string
  /** What was actually found, naming the specific object so it can be acted on. */
  detail: string
  // These carry `| undefined` explicitly because the project runs with
  // exactOptionalPropertyTypes, and rules routinely pass through a lookup that may
  // legitimately return undefined (a section that is absent has no line number).
  /** Where in the config, e.g. `firewall policy 12` or `system interface port1`. */
  location?: string | undefined
  /** 1-based source line, when the finding maps to one. */
  line?: number | undefined
  /** CIS Benchmark item number only. */
  cis?: string | undefined
  /** PCI DSS v4.0 requirement number only. */
  pci?: string | undefined
  /** Commands that fix it, in our own words. */
  remediation: string
}

export interface Rule {
  id: string
  /** Short human name, used in the rule list and in the coverage page. */
  title: string
  run(config: FgConfig): Finding[]
}

/** Convenience for rules, so each one does not repeat the ruleId. */
export function finding(
  rule: Pick<Rule, 'id'>,
  init: Omit<Finding, 'ruleId'>,
): Finding {
  return { ruleId: rule.id, ...init }
}
